export default class Utils {
    static isObject(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null && !this.isArray(value);
    }

    static isArray(value: unknown): value is unknown[] {
        return Array.isArray(value);
    }

    static isPrimitive(value: unknown): value is string | number | boolean | bigint | symbol {
        return !this.isObject(value) && !this.isArray(value) && value !== null && value !== undefined;
    }

    static deepEqual(obj1: unknown, obj2: unknown): boolean {
        if (obj1 === obj2) {
            return true;
        }

        if (Utils.isArray(obj1) && Utils.isArray(obj2)) {
            if (obj1.length !== obj2.length) return false;
            for (let i = 0; i < obj1.length; i++) {
                if (!Utils.deepEqual(
                    obj1[i],
                    obj2[i]
                )) return false;
            }
            return true;
        }

        if (
            typeof obj1 !== 'object'
            || obj1 === null
            || typeof obj2 !== 'object'
            || obj2 === null
        ) {
            return false;
        }

        const obj1Typed = obj1 as Record<string, unknown>;
        const obj2Typed = obj2 as Record<string, unknown>;

        const keys1 = Object.keys(obj1Typed);
        const keys2 = Object.keys(obj2Typed);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (!keys2.includes(key) || !Utils.deepEqual(
                obj1Typed[key],
                obj2Typed[key]
            )) {
                return false;
            }
        }

        return true;
    }

    static deepClone<T>(target: T, hash = new WeakMap()): T {
        if (target === null || typeof target !== 'object') {
            return target;
        }

        if (Utils.isObject(target)) {
            if (hash.has(target)) {
                return hash.get(target) as T;
            }
        }

        let clone: T;

        if (target instanceof Date) {
            clone = new Date(target.getTime()) as unknown as T;
        }
        else if (Utils.isArray(target)) {
            clone = [] as unknown as T;
            for (const value of target) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (clone as unknown as any[]).push(Utils.deepClone(
                    value,
                    hash
                ));
            }
        }
        else {
            clone = { ...target } as T;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const objectClone = clone as unknown as { [key: string]: any };

            for (const key of Object.keys(objectClone)) {
                objectClone[key] = Utils.deepClone(
                    objectClone[key],
                    hash
                );
            }
        }

        if (Utils.isObject(target)) {
            hash.set(
                target,
                clone
            );
        }

        return clone;
    }
}
